import { queryOptions, useQuery, type QueryClient } from "@tanstack/react-query";
import { isErrorBody } from "@purlink/shared/errors";
import { api, ApiError, unwrap, type SuccessBody } from "./api";

/** Error from a Better Auth endpoint ({ message, code } body). */
export class AuthError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "AuthError";
    this.status = status;
    this.code = code;
  }
}

async function authFetch<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/auth${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    if (isErrorBody(data)) throw new ApiError(res.status, data.error);
    const { message, code } = (data ?? {}) as { message?: string; code?: string };
    if (res.status === 429) {
      throw new AuthError(
        429,
        "Too many attempts. Please wait a minute and try again.",
        "RATE_LIMITED",
      );
    }
    throw new AuthError(res.status, message ?? "Something went wrong. Please try again.", code);
  }
  return data as T;
}

export type TwoFactorMethod = "totp" | "otp";

export type SignInResult =
  | { twoFactorRedirect: true; twoFactorMethods: TwoFactorMethod[] }
  | { twoFactorRedirect?: false; user: { id: string } };

/** Thin client for the Better Auth endpoints the SPA uses. */
export const authApi = {
  signIn: (email: string, password: string) =>
    authFetch<SignInResult>("/sign-in/email", { email, password, rememberMe: true }),
  signOut: () => authFetch<{ success: boolean }>("/sign-out"),
  verifyTotp: (code: string) => authFetch("/two-factor/verify-totp", { code }),
  sendOtp: () => authFetch("/two-factor/send-otp"),
  verifyOtp: (code: string) => authFetch("/two-factor/verify-otp", { code }),
  verifyBackupCode: (code: string) => authFetch("/two-factor/verify-backup-code", { code }),
  requestPasswordReset: (email: string) =>
    authFetch("/request-password-reset", { email, redirectTo: "/reset-password" }),
  resetPassword: (token: string, newPassword: string) =>
    authFetch("/reset-password", { token, newPassword }),
  changePassword: (currentPassword: string, newPassword: string) =>
    authFetch("/change-password", { currentPassword, newPassword, revokeOtherSessions: true }),
};

export type Me = SuccessBody<Awaited<ReturnType<(typeof api.api.me)["$get"]>>>;
export type MeUser = Me["user"];

export const meQuery = queryOptions({
  queryKey: ["me"] as const,
  queryFn: async (): Promise<Me | null> => {
    try {
      return await unwrap(api.api.me.$get());
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
  staleTime: 60_000,
});

export function useMe() {
  return useQuery(meQuery);
}

/** Like useMe but for routes guarded to signed-in users. */
export function useCurrentUser(): Me {
  const { data } = useQuery(meQuery);
  if (!data) throw new Error("useCurrentUser used outside an authenticated route");
  return data;
}

export async function refreshMe(queryClient: QueryClient) {
  await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
  return queryClient.fetchQuery(meQuery);
}

/** Signs out, clears every cached query and returns to the login page. */
export async function signOut(queryClient: QueryClient) {
  try {
    await authApi.signOut();
  } finally {
    queryClient.clear();
    window.location.assign("/login");
  }
}

/** Human message for an auth/API error. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof AuthError) return error.message;
  return "Something went wrong. Please try again.";
}

/** Field errors from an API VALIDATION error (for RHF setError). */
export function fieldErrorsOf(error: unknown): Record<string, string> {
  return error instanceof ApiError ? (error.body?.fields ?? {}) : {};
}
